import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, Select, TextField, Button, Spinner, Alert, EmptyState } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, AdminApiError, WAIVER_REASONS, type RunnerApplicationItem, type RunnerAppFilter, type RunnerRecruitCode, type RunnerCheckrWaiver } from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import { RunnerStageChip } from '../components/RunnerChips';
import { RUNNER_PIPELINE, RUNNER_OFF_PATH, METRO_LABEL, TRANSPORT_LABEL } from '../lib/runnerChips';

/**
 * Runner applications — the review queue (spec runner-portal.md §5). One row per
 * applicant with metro, transport mode and pipeline stage; "Review" opens the
 * pipeline screen. Recruit codes (GL Plus covers the Checkr fee for Rod-recruited
 * Runners) are minted from the panel below the queue.
 *
 * Everything here goes through admin-api → runner-api over HTTP (isolation
 * ruling); the portal never sees a Runner table.
 */

const GRID = '1.6fr 1.6fr 0.9fr 0.9fr 0.8fr 1.1fr 0.7fr';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

const STAGE_OPTIONS = [
  { value: 'open', label: 'Open (in pipeline)' },
  { value: 'all', label: 'All stages' },
  ...RUNNER_PIPELINE.map((s) => ({ value: s.status, label: s.label })),
  ...RUNNER_OFF_PATH.map((s) => ({ value: s.status, label: s.label })),
];
const METRO_OPTIONS = [
  { value: 'all', label: 'All metros' },
  { value: 'charlotte', label: 'Charlotte' },
  { value: 'cincinnati', label: 'Cincinnati' },
];

export default function RunnerApplications() {
  const caps = useCapabilities();
  const canView = caps.has('runner:read');
  const canReview = caps.has('runner:review');
  const canMint = caps.has('runner-recruit-code:mint');
  // 🔑 A DIFFERENT app entirely: admin-level on the Admin app, not the
  // Driver app. A reviewer who walks the pipeline never sees this panel.
  const canMintWaiver = caps.has('runner-checkr-waiver:mint');
  const navigate = useNavigate();

  const [stage, setStage] = useState<RunnerAppFilter>('open');
  const [metro, setMetro] = useState<'all' | 'charlotte' | 'cincinnati'>('all');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<RunnerApplicationItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listRunnerApplications({ status: stage, ...(metro === 'all' ? {} : { metro }) });
      setItems(res.applications);
      setCounts(res.counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Runner applications');
    } finally {
      setLoading(false);
    }
  }, [stage, metro]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((a) => `${a.firstName} ${a.lastName} ${a.email}`.toLowerCase().includes(q));
  }, [items, search]);

  const openCount = Object.entries(counts)
    .filter(([k]) => !['declined_prescreen', 'declined_checkr', 'live', 'withdrawn'].includes(k))
    .reduce((n, [, v]) => n + v, 0);

  if (caps.loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canView) return <Alert severity="error">You don’t have permission to view Runner applications.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Runner applications
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Runner onboarding pipeline — pre-screen the applicant, hand off to Checkr, walk them to go-live.
        {openCount > 0 && ` ${openCount} open · ${counts.live ?? 0} live.`}
      </Typography>

      {!canReview && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by name or email"
          ariaLabel="Search Runner applicants"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <Select
          ariaLabel="Filter by metro"
          label="Metro"
          native
          size="small"
          sx={{ width: 170 }}
          value={metro}
          onChange={(_e, v) => setMetro(v as 'all' | 'charlotte' | 'cincinnati')}
          options={METRO_OPTIONS}
        />
        <Select
          ariaLabel="Filter by stage"
          label="Stage"
          native
          size="small"
          sx={{ width: 220 }}
          value={stage}
          onChange={(_e, v) => setStage(v as RunnerAppFilter)}
          options={STAGE_OPTIONS}
        />
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Centered>
          <Spinner color="brand" />
        </Centered>
      ) : visible.length === 0 ? (
        <EmptyState icon="onlineordering" title="No Runner applications" body="No applicants match this filter." />
      ) : (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1.25, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            {['Applicant', 'Email', 'Metro', 'Transport', 'Applied', 'Stage', ''].map((h, i) => (
              <Typography key={i} variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>
                {h.toUpperCase()}
              </Typography>
            ))}
          </Box>
          {visible.map((app, idx) => (
            <Box
              key={app.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: 2,
                px: 2,
                py: 1.5,
                alignItems: 'center',
                borderBottom: idx < visible.length - 1 ? '1px solid #eef2f6' : 'none',
                '&:hover': { backgroundColor: '#fafbfc' },
              }}
            >
              <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>
                {app.firstName} {app.lastName}
                {app.checkrWaiverId && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: '#7A4A08', fontWeight: 700 }}>
                    WAIVED
                  </Typography>
                )}
                {app.recruitCodeId && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: '#7c3aed', fontWeight: 700 }}>
                    RECRUITED
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {app.email}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {METRO_LABEL[app.metro] ?? app.metro}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {TRANSPORT_LABEL[app.transportMode] ?? app.transportMode}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {fmtDate(app.submittedAt)}
              </Typography>
              <Box>
                <RunnerStageChip status={app.status} />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Button
                  ariaLabel={`Review ${app.firstName} ${app.lastName}`}
                  buttonText="Review"
                  variant="outlined"
                  size="small"
                  sx={NEUTRAL_BTN}
                  onClick={() => navigate(`/runner-applications/${app.id}`)}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <RecruitCodesPanel canMint={canMint} />
      <CheckrWaiversPanel canMint={canMintWaiver} />
    </Box>
  );
}

// ─── Recruit codes ────────────────────────────────────────────────────────────
function RecruitCodesPanel({ canMint }: { canMint: boolean }) {
  const [codes, setCodes] = useState<RunnerRecruitCode[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [state, setState] = useState<'unused' | 'consumed' | 'all'>('unused');

  const load = useCallback(async () => {
    try {
      const res = await adminApiClient.listRunnerRecruitCodes(state);
      setCodes(res.recruitCodes);
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof AdminApiError ? err.message : 'Failed to load recruit codes' });
    }
  }, [state]);

  useEffect(() => {
    void load();
  }, [load]);

  const mint = async () => {
    setBusy(true);
    setFlash(null);
    try {
      const res = await adminApiClient.mintRunnerRecruitCode({ note: note.trim() || undefined });
      setFlash({ kind: 'success', msg: `Minted ${res.recruitCode.code} — give it to the recruit; it is consumed at the Checkr handoff.` });
      setNote('');
      await load();
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof AdminApiError ? err.message : 'Mint failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 4, p: 2, border: '1px solid #e2e8f0', borderRadius: 2, backgroundColor: '#fff' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Recruit codes
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        One-time codes for Runners you recruit personally — GL Plus covers their Checkr fee. A code is consumed when the applicant is handed to Checkr, not when they apply.
      </Typography>
      {canMint && (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
          <TextField label="Note (who it's for)" ariaLabel="Recruit code note" size="small" value={note} onChange={(e) => setNote(e.target.value)} sx={{ flex: 1, minWidth: 240 }} disabled={busy} />
          <Button ariaLabel="Mint recruit code" buttonText={busy ? 'Minting…' : 'Mint code'} variant="contained" size="small" disabled={busy} onClick={() => void mint()} />
        </Box>
      )}
      {flash && (
        <Box sx={{ mb: 1.5 }}>
          <Alert severity={flash.kind}>{flash.msg}</Alert>
        </Box>
      )}
      <Select
        ariaLabel="Recruit code state"
        label="Show"
        native
        size="small"
        sx={{ width: 160, mb: 1.5 }}
        value={state}
        onChange={(_e, v) => setState(v as 'unused' | 'consumed' | 'all')}
        options={[
          { value: 'unused', label: 'Unused' },
          { value: 'consumed', label: 'Consumed' },
          { value: 'all', label: 'All' },
        ]}
      />
      {codes.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No codes.
        </Typography>
      ) : (
        codes.map((c) => (
          <Box key={c.id} sx={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1fr 1.4fr', gap: 2, py: 0.75, borderTop: '1px solid #eef2f6', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{c.code}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{c.note ?? '—'}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{c.expiresAt ? `exp ${fmtDate(c.expiresAt)}` : 'no expiry'}</Typography>
            <Typography variant="body2" sx={{ color: c.consumedAt ? '#15803d' : '#64748b' }}>
              {c.consumedAt ? `used by ${c.applications[0] ? `${c.applications[0].firstName} ${c.applications[0].lastName}` : 'applicant'}` : 'unused'}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
}

// ─── Checkr waiver codes ─────────────────────────────────────────────────────
//
// 🛑 Deliberately a SEPARATE panel from recruit codes, visible to a different
// set of people, because the two must never be confused. A recruit code is
// typed by the APPLICANT on the public form and only decides who pays the
// Checkr fee. A waiver code decides whether a check happens at all and must
// never leave this screen — a skip handed to the person it benefits is a
// forwardable bearer token that settles the question before any human has read
// the application.
function CheckrWaiversPanel({ canMint }: { canMint: boolean }) {
  const [codes, setCodes] = useState<RunnerCheckrWaiver[]>([]);
  const [reason, setReason] = useState<string>('known_to_founder');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [state, setState] = useState<'unused' | 'consumed' | 'all'>('unused');

  const load = useCallback(async () => {
    try {
      const res = await adminApiClient.listRunnerCheckrWaivers(state);
      setCodes(res.checkrWaivers);
    } catch {
      // Silent: a reviewer without runner:read never reaches this screen, and a
      // failed list must not bury the mint form in an error the minter cannot act on.
      setCodes([]);
    }
  }, [state]);

  useEffect(() => { void load(); }, [load]);

  const mint = async () => {
    setBusy(true);
    setFlash(null);
    try {
      const res = await adminApiClient.mintRunnerCheckrWaiver({ reason, note: note.trim() || undefined });
      setFlash({ kind: 'success', msg: `Minted ${res.checkrWaiver.code}. Paste it into the applicant's review screen — do NOT send it to them. One use${res.checkrWaiver.expiresAt ? `, expires ${fmtDate(res.checkrWaiver.expiresAt)}` : ''}.` });
      setNote('');
      await load();
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof AdminApiError ? err.message : 'Mint failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 4, p: 2, border: '1px solid #e8c48a', borderRadius: 2, backgroundColor: '#FFFDF7' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#7A4A08' }}>
        Background-check waivers
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        A waiver lets one applicant skip the Checkr background check entirely — no check is ordered and no report ever
        exists for them. Paste the code into that applicant's review screen while they are in pre-screen review. It works
        once, it expires, and it is <strong>never given to the applicant</strong>.
      </Typography>
      {canMint ? (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
          <Select
            ariaLabel="Waiver reason"
            label="Reason"
            native
            size="small"
            sx={{ width: 260 }}
            value={reason}
            onChange={(_e, v) => setReason(v as string)}
            options={WAIVER_REASONS.map((r) => ({ value: r.value, label: r.label }))}
          />
          <TextField label="Note (who and why)" ariaLabel="Waiver note" size="small" value={note} onChange={(e) => setNote(e.target.value)} sx={{ flex: 1, minWidth: 240 }} disabled={busy} />
          <Button ariaLabel="Mint waiver code" buttonText={busy ? 'Minting…' : 'Mint waiver'} variant="contained" size="small" disabled={busy} onClick={() => void mint()} />
        </Box>
      ) : (
        <Box sx={{ mb: 1.5 }}>
          <Alert severity="info">Minting a waiver needs admin access on the Admin app — the same level as staff management.</Alert>
        </Box>
      )}
      {flash && (
        <Box sx={{ mb: 1.5 }}>
          <Alert severity={flash.kind}>{flash.msg}</Alert>
        </Box>
      )}
      <Select
        ariaLabel="Waiver state"
        label="Show"
        native
        size="small"
        sx={{ width: 160, mb: 1.5 }}
        value={state}
        onChange={(_e, v) => setState(v as 'unused' | 'consumed' | 'all')}
        options={[
          { value: 'unused', label: 'Unused' },
          { value: 'consumed', label: 'Used' },
          { value: 'all', label: 'All' },
        ]}
      />
      {codes.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No waiver codes.</Typography>
      ) : (
        codes.map((c) => (
          <Box key={c.id} sx={{ display: 'grid', gridTemplateColumns: '1.3fr 1.2fr 1.6fr 1fr 1.4fr', gap: 2, py: 0.75, borderTop: '1px solid #f0e6d2', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{c.code}</Typography>
            <Typography variant="body2" sx={{ color: '#7A4A08' }}>
              {WAIVER_REASONS.find((r) => r.value === c.reason)?.label ?? c.reason}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{c.note ?? '—'}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{c.expiresAt ? `exp ${fmtDate(c.expiresAt)}` : 'no expiry'}</Typography>
            <Typography variant="body2" sx={{ color: c.consumedAt ? '#7A4A08' : '#64748b' }}>
              {c.consumedAt
                ? `used on ${c.applications[0] ? `${c.applications[0].firstName} ${c.applications[0].lastName}` : 'an applicant'}`
                : 'unused'}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
}
